import { useMemo } from 'react';
import type { StyleObject } from 'styletron-react';
import { CSS_VARIABLE_STAGE_ROTATION, CSS_VARIABLE_STAGE_VISIBLE_SCALE } from '../../constants/stage-css-variables';

/**
 * Hook for positioning DOM elements over the stage that maintain constant visual size
 * despite stage zoom/pan/rotation transforms.
 *
 * Use this when you need to render DOM elements (like buttons, overlays, etc.) that:
 * - Should be positioned relative to stage coordinates (e.g., at an annotation's position)
 * - Should maintain constant visual size regardless of zoom level
 * - Should stay upright regardless of stage rotation
 */

export const useStageOverlayPositioning = () => {
  return useMemo(() => ({
    getOverlayStyles: ({
      stageX,
      stageY,
      translateX = '0',
      translateY = '0',
      transformOrigin = 'top left'
    }: {
      stageX: number;
      stageY: number;
      translateX?: string;
      translateY?: string;
      transformOrigin?: string;
    }): StyleObject => ({
      position: 'absolute',
      top: `${stageY}px`,
      left: `${stageX}px`,
      // Transform order (applied right-to-left):
      // 1. translate - moves the element in scaled space (maintains visual distance)
      // 2. rotate - counter-rotates to keep element upright
      // 3. scale - inverse scales to maintain constant visual size
      transform: `scale(var(${CSS_VARIABLE_STAGE_VISIBLE_SCALE})) rotate(calc(-1 * var(${CSS_VARIABLE_STAGE_ROTATION}))) translate(${translateX}, ${translateY})`,
      transformOrigin,
      pointerEvents: 'all'
    })
  }), []);
};